import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Label, SelectArrow } from 'modules/ui';
import { useDidMount } from 'modules/core';
import styles from './DatePicker.module.scss';

const DAY_END = { hour: 20, minute: 0, second: 0 };
const DAY_START = { hour: 4, minute: 0, second: 0 };

function DatePickerContainer({ children }) {
  return (
    <div
      style={{
        margin: '16px 0 36px',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
    >
      {children}
    </div>
  );
}

export default function DatePickerInput({
  name,
  value,
  minDate = moment(),
  maxDate,
  error,
  label,
  placeholder = 'Pick a date',
  width,
  setFormValue,
  arrowClassName,
  setAsDayStart = false,
  required,
  optional,
  disabled,
  ...otherProps
}) {
  const didMount = useDidMount();

  const [selectedDate, setSelectedDate] = useState(
    value ? moment(value) : null,
  );

  // cannot be inside useCallback without causing state persistance issues inside setFormValue (useForm setValue)
  const handleSetDate = (isoDate) => {
    setSelectedDate(moment(isoDate));

    if (setFormValue) {
      setFormValue(name, isoDate);
    }
  };

  useEffect(
    () => {
      if (didMount) {
        handleSetDate(value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  ); // TODO: didMount causes "changes" to be registered when loading forms

  function handleDateChange(date) {
    date.set(setAsDayStart ? DAY_START : DAY_END);

    const isoDate = date.toISOString(true);
    handleSetDate(isoDate);
  }

  const hasErrorMessage = typeof error === 'string';

  return (
    <DatePickerContainer>
      {label && (
        <Label
          label={label}
          required={required}
          optional={optional}
          error={error}
          disabled={disabled}
        />
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          displayStaticWrapperAs="desktop"
          value={selectedDate}
          minDate={minDate}
          maxDate={maxDate}
          onChange={handleDateChange}
          placeholder={placeholder}
          slots={{
            openPickerIcon: SelectArrow,
          }}
          slotProps={{
            openPickerButton: {
              className: styles.openPickerButton,
            },
            popper: {
              className: styles.popper,
              disablePortal: true,
            },
            textField: {
              className: styles.textField,
              placeholder,
              style: otherProps.style,
            },
          }}
          {...otherProps}
        />
      </LocalizationProvider>
      {hasErrorMessage && (
        <div id={`${name}-error`} className={styles.errorMessage}>
          {error}
        </div>
      )}
    </DatePickerContainer>
  );
}
